import { useAlert } from "contexts/alertContext";
import { handleError } from "helpers/utils";
import { useState, useCallback, useEffect } from "react";
import { useQueryClient } from "react-query";

type FetchSectionProps<T, U> = {
    section:string;
  fetchData: () => Promise<T>;
  fetchPermission: () => Promise<U>;
  processResponse?: (data: T) => T;
  onSuccess?: (data: T) => void; 
};

const useFetchSection = <T, U>({ 
    section,
  fetchData, 
  fetchPermission, 
  processResponse = (data: T) => data, // Optional processing of fetched data
  onSuccess
}: FetchSectionProps<T, U>) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<T | null>(null);
  const [permission, setPermission] = useState<U | null>(null);
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const { setAlert } = useAlert();

  const fetchSection = useCallback(async () => {
    try {
      // Fetch data and permission concurrently
      const [dataResponse, permissionResponse] = await Promise.all([
        queryClient.fetchQuery(`get ${section}`, fetchData),
        fetchPermission(),
      ]);

      // Process and set the fetched data
      setData(processResponse(dataResponse));
      if (onSuccess) {
        onSuccess(processResponse(dataResponse));
      }
      setPermission(permissionResponse);
      

    } catch (error: any) {
      // Handle errors
      setError(error.message || "Something went wrong");
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
 
    }
    finally {
        setLoading(false);
       
      }

    // eslint-disable-next-line
  }, [queryClient, fetchData, fetchPermission, processResponse, setAlert]);

  useEffect(() => {
    fetchSection();
    // eslint-disable-next-line
  }, []);
  

  return { loading, data, error, permission, fetchSection,revalidate:fetchSection };
};

export default useFetchSection;
